import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Gift, Calendar, Trophy, TrendingUp } from 'lucide-react';
import { usePractitionerCredits } from '@/hooks/usePractitionerCredits';
import { usePractitionerMilestones } from '@/hooks/usePractitionerMilestones';

interface PerksOverviewProps {
  compact?: boolean;
}

export const PerksOverview: React.FC<PerksOverviewProps> = ({ compact = false }) => {
  const { credits, monthlyFreeAvailable } = usePractitionerCredits();
  const { milestoneProgress, totalCreditsEarned, isLoading } = usePractitionerMilestones();

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-3">
              <div className="h-4 bg-muted rounded w-3/4"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-muted rounded mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (compact) {
    return (
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            <div>
              <p className="text-sm font-medium">{monthlyFreeAvailable}</p>
              <p className="text-xs text-muted-foreground">Free Left</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-3">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-primary" />
            <div>
              <p className="text-sm font-medium">{totalCreditsEarned}</p>
              <p className="text-xs text-muted-foreground">Milestone Credits</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Monthly Free Credits */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-medium">Monthly Free Sessions</CardTitle>
          <Calendar className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline space-x-3">
            <div className="text-2xl font-bold text-primary">
              {monthlyFreeAvailable}
            </div>
            <div className="text-sm text-muted-foreground">
              of {credits?.monthly_free_credits || 10} remaining
            </div>
          </div>
          <Progress 
            value={(monthlyFreeAvailable / (credits?.monthly_free_credits || 10)) * 100} 
            className="mt-2" 
          />
          <p className="text-xs text-muted-foreground mt-2">
            Resets monthly • Use for demos or personal exploration
          </p>
        </CardContent>
      </Card>

      {/* Milestone Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Milestone Rewards
          </CardTitle>
          <CardDescription>
            Earn bonus credits as your practice grows
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {milestoneProgress.map((milestone) => (
            <div key={milestone.id} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-sm">
                    {milestone.threshold} Active Clients
                  </h4>
                  {milestone.isAchieved && (
                    <Badge variant="default" className="text-xs">
                      <Gift className="h-3 w-3 mr-1" />
                      Earned
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  +{milestone.reward_credits} credits
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Progress 
                  value={milestone.progressPercentage} 
                  className="flex-1" 
                />
                <span className="text-xs text-muted-foreground min-w-[60px]">
                  {milestone.currentProgress}/{milestone.threshold}
                </span>
              </div>
            </div>
          ))}
          
          {totalCreditsEarned > 0 && (
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Total Milestone Credits Earned:</span>
                <Badge variant="secondary" className="font-medium">
                  +{totalCreditsEarned} credits
                </Badge>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Volume Bonus Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Volume Purchase Bonuses
          </CardTitle>
          <CardDescription>
            Get extra credits when you buy in bulk
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
              <span className="font-medium">500 Credits Package</span>
              <Badge variant="outline">+25 Bonus (525 total)</Badge>
            </div>
            <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
              <span className="font-medium">1,000 Credits Package</span>
              <Badge variant="outline">+50 Bonus (1,050 total)</Badge>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Bonus credits are automatically added to your account with each purchase
          </p>
        </CardContent>
      </Card>
    </div>
  );
};